import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX } = FiIcons;

const AgencyModal = ({ isOpen, onClose, onSave, agency = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    niche_type: 'normal',
    payment_type: 'prepaid', // 'prepaid' or 'postpaid'
    total_articles: '',
    total_price: '',
    price_per_article: '',
    remaining_articles: '',
    billing_status: 'pending', // 'pending', 'paid', 'overdue'
    billing_cycle: 'monthly', // 'weekly', 'monthly', 'quarterly'
    credit_limit: '', // Max unpaid amount for postpaid clients
    last_billed_at: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (agency) {
      setFormData({
        name: agency.name || '',
        contact_person: agency.contact_person || '',
        email: agency.email || '',
        phone: agency.phone || '',
        niche_type: agency.niche_type || 'normal',
        payment_type: agency.payment_type || 'prepaid',
        total_articles: agency.total_articles || '',
        total_price: agency.total_price || '',
        price_per_article: agency.price_per_article || '',
        remaining_articles: agency.remaining_articles || '',
        billing_status: agency.billing_status || 'pending',
        billing_cycle: agency.billing_cycle || 'monthly',
        credit_limit: agency.credit_limit || '',
        last_billed_at: agency.last_billed_at || null
      });
    } else {
      setFormData({
        name: '',
        contact_person: '',
        email: '',
        phone: '',
        niche_type: 'normal',
        payment_type: 'prepaid',
        total_articles: '',
        total_price: '',
        price_per_article: '',
        remaining_articles: '',
        billing_status: 'pending',
        billing_cycle: 'monthly',
        credit_limit: '',
        last_billed_at: null
      });
    }
  }, [agency]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };

      // Auto-calculate price per article when total articles or total price changes
      if ((name === 'total_articles' || name === 'total_price') && formData.payment_type === 'prepaid') {
        const articles = name === 'total_articles' ? parseInt(value) : parseInt(updated.total_articles);
        const price = name === 'total_price' ? parseFloat(value) : parseFloat(updated.total_price);
        
        if (articles && price) {
          updated.price_per_article = (price / articles).toFixed(2);
        }
      }

      // Set remaining articles to total articles for new prepaid agencies
      if (name === 'total_articles' && !agency && formData.payment_type === 'prepaid') {
        updated.remaining_articles = value;
      }

      // Clear prepaid-specific fields when switching to postpaid
      if (name === 'payment_type' && value === 'postpaid') {
        updated.total_articles = '';
        updated.total_price = '';
        updated.remaining_articles = '';
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Convert string numbers to actual numbers
      const processedData = {
        ...formData,
        total_articles: formData.payment_type === 'prepaid' ? parseInt(formData.total_articles) || 0 : 0,
        total_price: formData.payment_type === 'prepaid' ? parseFloat(formData.total_price) || 0 : 0,
        price_per_article: parseFloat(formData.price_per_article) || 0,
        remaining_articles: formData.payment_type === 'prepaid' ? parseInt(formData.remaining_articles) || 0 : 0,
        credit_limit: formData.payment_type === 'postpaid' ? parseFloat(formData.credit_limit) || 0 : 0
      };

      await onSave(processedData);
      onClose();
    } catch (error) {
      console.error('Error saving agency:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {agency ? 'Edit Agency' : 'Add New Agency'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Agency Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Person *
            </label>
            <input
              type="text"
              name="contact_person"
              value={formData.contact_person}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Niche Type
            </label>
            <select
              name="niche_type"
              value={formData.niche_type}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="normal">Normal Niche</option>
              <option value="special">Special Niche</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Type *
            </label>
            <select
              name="payment_type"
              value={formData.payment_type}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="prepaid">Prepaid (Pay upfront)</option>
              <option value="postpaid">Post-Payment (Bill after publishing)</option>
            </select>
          </div>

          {formData.payment_type === 'prepaid' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Articles *
                </label>
                <input
                  type="number"
                  name="total_articles"
                  value={formData.total_articles}
                  onChange={handleChange}
                  required
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Price (LEI) *
                </label>
                <input
                  type="number"
                  name="total_price"
                  value={formData.total_price}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {agency && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Remaining Articles *
                  </label>
                  <input
                    type="number"
                    name="remaining_articles"
                    value={formData.remaining_articles}
                    onChange={handleChange}
                    required
                    min="0"
                    max={formData.total_articles}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Billing Cycle
                </label>
                <select
                  name="billing_cycle"
                  value={formData.billing_cycle}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Credit Limit (LEI)
                </label>
                <input
                  type="number"
                  name="credit_limit"
                  value={formData.credit_limit}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Maximum unpaid amount"
                />
              </div>

              {agency && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Billing Status
                  </label>
                  <select
                    name="billing_status"
                    value={formData.billing_status}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>
              )}
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price per Article (LEI) *
            </label>
            <input
              type="number"
              name="price_per_article"
              value={formData.price_per_article}
              onChange={handleChange}
              required
              min="0"
              step="0.01"
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                formData.payment_type === 'prepaid' && formData.total_articles && formData.total_price 
                  ? 'bg-gray-50 text-gray-600' 
                  : ''
              }`}
              readOnly={formData.payment_type === 'prepaid' && formData.total_articles && formData.total_price}
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : (agency ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AgencyModal;